import React, { lazy, Suspense, useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './styles.module.scss';
import Heading from 'components/shared/heading';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

const SectionSlide = ({ section }) => {
  const ref = useRef(null);
  const wistiaPlayerRef = useRef();
  const [sectionRef, inView] = useInView({ threshold: 0.4 });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Use Framer Motion's useScroll to track the element's position in the viewport
  // "start end" means when the top of the element hits the bottom of the viewport
  // "center center" means when the center of the element hits the center of the viewport
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'center center'],
  });

  // Add spring smoothing to the scroll progress
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 70,
    damping: 20,
    restDelta: 0.001,
  });

  // Transform smooth progress to animation values
  // Original logic was 10% to 60% visibility.
  // Adjusted scale start from 0.8 to 0.9 for subtler effect.
  const scale = useTransform(smoothProgress, [0.1, 0.6], [0.8, 1]);

  // Parallax: Title moves faster (from further down), Content moves slower
  // Animation Timing:
  // - Title (top) animates early [0.1, 0.6]
  // - Content (bottom) animates late [0.55, 0.85] so it fades in when actually visible

  const placeStyleToWistia = useCallback((wistiaElementParam) => {
    const wistiaElement = wistiaElementParam; // || wistiaPlayerRef.current;
    // Find the Shadow DOM
    const { shadowRoot } = wistiaElement;
    if (shadowRoot) {
      // Create a style element to inject our CSS
      const styleEl = document.createElement('style');
      styleEl.textContent = `
          /* This CSS is now safely inside the Shadow DOM */
          video {
            
            object-fit: cover !important;
            
          }
         
          .click-for-sound-btn {
            display: none !important;
          }
      `;
      shadowRoot.appendChild(styleEl);
    }
  }, []);

  const handleApiReady = useCallback(
    (event) => {
      const player = event?.target;
      if (!player) return;
      // wistiaPlayerRef.current = player;
      placeStyleToWistia(player);
    },
    [placeStyleToWistia]
  );

  useEffect(() => {
    if (inView) {
      wistiaPlayerRef?.current?.play?.();
    } else {
      wistiaPlayerRef?.current?.pause?.();
    }
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      wistiaPlayerRef?.current?.pause?.();
    };
  }, [inView]);

  return (
    <motion.div
      style={{ scale }}
      className={styles.slide}
      ref={(node) => {
        ref.current = node;
        sectionRef(node);
      }}
    >
      {/* Background Image/Video */}
      {section.image?.id && (
        <div className={styles.backgroundImage}>
          <div
            className={styles.imageWrapper}
            id={section.videoId ? undefined : `bs-img-box-${section.image.id}`}
          >
            {isClient && section.videoId && (
              <Suspense fallback={null}>
                <WistiaPlayerLazy
                  mediaId={section.videoId}
                  ref={wistiaPlayerRef}
                  onApiReady={handleApiReady}
                  autoPlay={false}
                  playlistLoop
                  muted
                  className={styles.wistiaPlayer}
                  poster={false}
                  swatch={false}
                  controls={false}
                  resumable
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Suspense>
            )}
          </div>
        </div>
      )}

      {/* Overlay Content */}
      <div className={styles.overlayContent}>
        <div className={styles.content}>
          <div className={styles.sectionHeader}>
            {(section.number || section.eyebrow) && (
              <div className={styles.eyebrow}>
                {section.number && <span className={styles.eyebrowNumber}>{section.number}</span>}
                {section.eyebrow && <span className={styles.eyebrowSeparator}>—</span>}
                {section.eyebrow && <span className={styles.eyebrowLabel}>{section.eyebrow}</span>}
              </div>
            )}
            {section.title && (
              <Heading className={styles.headline} tag="h3" size="lg">
                {section.title}
              </Heading>
            )}
          </div>

          {(section.leftTitle ||
            section.leftDescription ||
            section.rightTitle ||
            section.rightDescription) && (
            <div className={styles.columns}>
              <div className={styles.columnLeft}>
                {(section.leftTitle || section.leftDescription) && (
                  <>
                    <div className={styles.columnTitle}>{section.leftTitle}</div>
                    {section.leftDescription && (
                      <div
                        className={styles.columnBody}
                        dangerouslySetInnerHTML={{ __html: section.leftDescription }}
                      />
                    )}
                  </>
                )}
              </div>
              <div className={styles.columnRight}>
                {(section.rightTitle || section.rightDescription) && (
                  <>
                    <div className={styles.columnTitle}>{section.rightTitle}</div>
                    {section.rightDescription && (
                      <div
                        className={styles.columnBody}
                        dangerouslySetInnerHTML={{ __html: section.rightDescription }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const BenefitsSections = ({
  title = null,
  titleTag = 'h2',
  description = null,
  sections = [],
  theme = '',
}) => {
  const containerRef = useRef(null);

  // Inject images once when sections arrive
  useEffect(() => {
    let timeoutId = null;

    if (sections && sections.length > 0 && containerRef?.current) {
      timeoutId = setTimeout(() => {
        sections.forEach((section) => {
          if (!section?.image?.id) return;
          const parent = containerRef?.current?.parentElement;
          const source = parent?.querySelector(`#bs-img-${section.image.id}`);
          const target = parent?.querySelector(`#bs-img-box-${section.image.id}`);

          if (source && target) {
            target.innerHTML = '';
            target.appendChild(source.cloneNode(true));
          }
        });
      }, 100);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [sections]);

  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <section
      ref={containerRef}
      className={cn(styles.wrapper, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      {(title || description) && (
        <header className={styles.header}>
          {title && (
            <Heading className={styles.title} tag={titleTag} size="xl">
              {title}
            </Heading>
          )}
          {description && (
            <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </header>
      )}
      {sections.map((section, index) => (
        <SectionSlide key={section.id || index} section={section} theme={theme} />
      ))}
    </section>
  );
};

SectionSlide.propTypes = {
  section: PropTypes.shape({
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    leftTitle: PropTypes.string,
    leftDescription: PropTypes.string,
    rightTitle: PropTypes.string,
    rightDescription: PropTypes.string,
    image: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    videoId: PropTypes.string,
  }).isRequired,
  theme: PropTypes.oneOf(['light', 'dark', null]),
};

BenefitsSections.propTypes = {
  title: PropTypes.string,
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  description: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark', null]),
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      eyebrow: PropTypes.string,
      title: PropTypes.string,
      leftTitle: PropTypes.string,
      leftDescription: PropTypes.string,
      rightTitle: PropTypes.string,
      rightDescription: PropTypes.string,
      image: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
      videoId: PropTypes.string,
    })
  ),
};

export default BenefitsSections;
